import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { paymentRoutingService } from '@services/paymentRoutingService';
import companyService from '@services/companyService';
import {
  CarrierServiceOption,
  PaymentRoutingConfigPayload,
  PaymentRoutingConfigResult,
  PaymentRoutingMode,
  ROUTING_MODE_CAP_PRIORITY,
  ROUTING_MODE_WEIGHTED,
} from '@/types/paymentRouting';
import { Company, formatCnpj } from '@/types/company';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import { RoutingCitiesEditor } from './RoutingCitiesEditor';
import { CarrierBlocksEditor } from './CarrierBlocksEditor';
import { WeightedRoutingPanel } from './WeightedRoutingPanel';
import { RoutingSimulatorCard } from './RoutingSimulatorCard';
import { RoutingDecisionsCard } from './RoutingDecisionsCard';
import {
  carrierLabel,
  toWeightedInput,
  validateWeightedRules,
  weightDraftToNumber,
} from './weightedRoutingUtils';

/** Regra em edição local (teto como string para permitir campo vazio = sem teto). */
interface DraftCity {
  city_name: string;
  state_uf: string | null;
}

interface DraftRule {
  payment_routing_rule_id: number; // 0 = regra nova (ainda não persistida)
  company_id: number;
  company_name: string;
  company_document: string;
  is_default: boolean;
  enabled: boolean;
  monthly_revenue_cap: string; // '' = sem teto
  cap_trigger_percent: number;
  cap_reached_month: string | null;
  cap_reached_on: string | null;
  cities: DraftCity[];
  weight: string; // '' = não configurado
  carrier_blocks: { provider_id: number; provider_service_id: number | null }[];
  captured_this_month: number;
}

const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const PaymentRoutingTab: React.FC = () => {
  const [routingEnabled, setRoutingEnabled] = useState(false);
  const [routingMode, setRoutingMode] = useState<PaymentRoutingMode>(ROUTING_MODE_CAP_PRIORITY);
  const [carrierServices, setCarrierServices] = useState<CarrierServiceOption[]>([]);
  const [carrierLoadError, setCarrierLoadError] = useState<string | null>(null);
  const [rules, setRules] = useState<DraftRule[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [clearingCap, setClearingCap] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [newCompanyId, setNewCompanyId] = useState(0);
  // Inputs de nova cidade, por empresa
  const [cityDrafts, setCityDrafts] = useState<Record<number, { name: string; uf: string }>>({});

  const applyResult = (result: PaymentRoutingConfigResult) => {
    setRoutingEnabled(result.config.routing_enabled);
    setRoutingMode(result.config.routing_mode === ROUTING_MODE_WEIGHTED ? ROUTING_MODE_WEIGHTED : ROUTING_MODE_CAP_PRIORITY);
    setRules(result.config.rules.map(r => ({
      payment_routing_rule_id: r.payment_routing_rule_id,
      company_id: r.company_id,
      company_name: r.company_name,
      company_document: r.company_document,
      is_default: r.is_default,
      enabled: r.enabled,
      monthly_revenue_cap: r.monthly_revenue_cap != null ? String(r.monthly_revenue_cap) : '',
      cap_trigger_percent: r.cap_trigger_percent,
      cap_reached_month: r.cap_reached_month,
      cap_reached_on: r.cap_reached_on,
      cities: r.cities.map(c => ({ city_name: c.city_name, state_uf: c.state_uf })),
      weight: r.weight != null ? String(r.weight) : '',
      carrier_blocks: (r.carrier_blocks ?? []).map(b => ({
        provider_id: b.provider_id,
        provider_service_id: b.provider_service_id ?? null,
      })),
      captured_this_month: r.captured_this_month ?? 0,
    })));
    setWarnings(result.warnings ?? []);
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await paymentRoutingService.getConfig();
      applyResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const comps = await companyService.getCompanies();
      setCompanies(comps);
    } catch { /* silently ignore lookup errors */ }
  };

  const loadCarrierServices = async () => {
    try {
      setCarrierLoadError(null);
      setCarrierServices(await paymentRoutingService.getCarrierServices());
    } catch (err) {
      setCarrierLoadError(err instanceof Error ? err.message : 'Erro ao carregar transportadoras');
    }
  };

  useEffect(() => {
    loadConfig();
    loadCompanies();
    loadCarrierServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRule = (companyId: number, changes: Partial<DraftRule>) => {
    setRules(prev => prev.map(r => (r.company_id === companyId ? { ...r, ...changes } : r)));
  };

  const setDefaultCompany = (companyId: number) => {
    setRules(prev => prev.map(r => ({ ...r, is_default: r.company_id === companyId })));
  };

  const addCity = (companyId: number) => {
    const draft = cityDrafts[companyId];
    const name = (draft?.name ?? '').trim();
    const uf = (draft?.uf ?? '').trim().toUpperCase();
    if (!name) return;
    setRules(prev => prev.map(r => {
      if (r.company_id !== companyId) return r;
      const exists = r.cities.some(
        c => c.city_name.toLowerCase() === name.toLowerCase() && (c.state_uf ?? '') === uf
      );
      if (exists) return r;
      return { ...r, cities: [...r.cities, { city_name: name, state_uf: uf || null }] };
    }));
    setCityDrafts(prev => ({ ...prev, [companyId]: { name: '', uf: '' } }));
  };

  const removeCity = (companyId: number, index: number) => {
    setRules(prev => prev.map(r =>
      r.company_id === companyId
        ? { ...r, cities: r.cities.filter((_, i) => i !== index) }
        : r
    ));
  };

  const addCompanyRule = () => {
    if (!newCompanyId) return;
    const company = companies.find(c => c.company_id === newCompanyId);
    if (!company || rules.some(r => r.company_id === newCompanyId)) return;
    setRules(prev => [...prev, {
      payment_routing_rule_id: 0,
      company_id: company.company_id,
      company_name: company.name,
      company_document: company.document,
      is_default: prev.length === 0,
      enabled: true,
      monthly_revenue_cap: '',
      cap_trigger_percent: 100,
      cap_reached_month: null,
      cap_reached_on: null,
      cities: [],
      weight: '1',
      carrier_blocks: [],
      captured_this_month: 0,
    }]);
    setNewCompanyId(0);
  };

  const weightedValidation = useMemo(
    () => validateWeightedRules(
      rules.map(toWeightedInput),
      (pid, sid) => carrierLabel(carrierServices, pid, sid),
    ),
    [rules, carrierServices],
  );
  const saveBlocked = routingMode === ROUTING_MODE_WEIGHTED && weightedValidation.errors.length > 0;

  const buildPayload = (): PaymentRoutingConfigPayload => ({
    routing_enabled: routingEnabled,
    routing_mode: routingMode,
    rules: rules.map(r => ({
      company_id: r.company_id,
      is_default: r.is_default,
      enabled: r.enabled,
      monthly_revenue_cap: r.monthly_revenue_cap.trim() === ''
        ? null
        : Number(r.monthly_revenue_cap.replace(',', '.')),
      cap_trigger_percent: r.cap_trigger_percent,
      cities: r.cities.map(c => ({ city_name: c.city_name, state_uf: c.state_uf })),
      weight: (() => {
        const w = weightDraftToNumber(r.weight);
        return w == null || Number.isNaN(w) ? null : w;
      })(),
      carrier_blocks: r.carrier_blocks,
    })),
  });

  const handleSave = async () => {
    if (saveBlocked) {
      setError('Corrija os erros do balanceamento por peso antes de salvar.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const result = await paymentRoutingService.updateConfig(buildPayload());
      applyResult(result);
      setSuccessMessage('Configuração de roteamento salva com sucesso.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleReloadCache = async () => {
    try {
      setReloading(true);
      setError(null);
      setSuccessMessage(null);
      const message = await paymentRoutingService.reloadCache();
      setSuccessMessage(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao recarregar cache');
    } finally {
      setReloading(false);
    }
  };

  const handleClearCap = async (companyId: number) => {
    try {
      setClearingCap(companyId);
      setError(null);
      setSuccessMessage(null);
      await paymentRoutingService.clearCap(companyId);
      setSuccessMessage('Empresa reativada no roteamento.');
      await loadConfig();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reativar empresa');
    } finally {
      setClearingCap(null);
    }
  };

  const renderCities = (companyId: number, weighted: boolean) => {
    const rule = rules.find(r => r.company_id === companyId);
    if (!rule) return null;
    return (
      <RoutingCitiesEditor
        cities={rule.cities}
        draft={cityDrafts[companyId] ?? { name: '', uf: '' }}
        onDraftChange={draft => setCityDrafts(prev => ({ ...prev, [companyId]: draft }))}
        onAdd={() => addCity(companyId)}
        onRemove={index => removeCity(companyId, index)}
        help={weighted
          ? 'Vazio = sem restrição geográfica; com cidades, pedidos de outras cidades nunca vão para esta empresa'
          : 'Vazio = sem restrição geográfica; com cidades, pedidos de outras cidades vão para a empresa padrão'}
      />
    );
  };

  const companiesWithoutRule = companies.filter(
    c => !rules.some(r => r.company_id === c.company_id)
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(2)].map((_, i) => (
          <Card key={i}>
            <CardContent>
              <div className="h-4 bg-slate-200 rounded w-48 animate-pulse mb-3" />
              <div className="h-4 bg-slate-200 rounded w-72 animate-pulse" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between text-sm text-green-800">
          <span>{successMessage}</span>
          <button
            className="ml-4 text-green-600 hover:text-green-800 underline text-xs"
            onClick={() => setSuccessMessage(null)}
          >
            Fechar
          </button>
        </div>
      )}

      {warnings.map((warning, i) => (
        <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
          <span>{warning}</span>
        </div>
      ))}

      {/* Configuração global */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={routingEnabled}
                onChange={e => setRoutingEnabled(e.target.checked)}
              />
              <span className="text-sm font-medium text-slate-700">
                Roteamento dinâmico habilitado
              </span>
            </label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReloadCache}
                loading={reloading}
                icon={<RefreshCw size={14} />}
              >
                Recarregar cache do PaymentHub
              </Button>
              <PermissionGuard permission="receiving_hub.write">
                <Button
                  size="sm"
                  onClick={handleSave}
                  loading={saving}
                  disabled={saveBlocked}
                  icon={<Save size={14} />}
                >
                  Salvar configuração
                </Button>
              </PermissionGuard>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-slate-700">Modo de roteamento:</span>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="radio"
                name="payment-routing-mode"
                className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={routingMode === ROUTING_MODE_CAP_PRIORITY}
                onChange={() => setRoutingMode(ROUTING_MODE_CAP_PRIORITY)}
              />
              Prioridade por teto (atual)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="radio"
                name="payment-routing-mode"
                className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={routingMode === ROUTING_MODE_WEIGHTED}
                onChange={() => setRoutingMode(ROUTING_MODE_WEIGHTED)}
              />
              Balanceamento por peso
            </label>
          </div>

          {routingMode === ROUTING_MODE_CAP_PRIORITY ? (
            <p className="text-xs text-slate-500 mt-2">
              Com o roteamento desabilitado, todos os pagamentos são capturados pela empresa padrão.
              Habilitado, empresas com teto configurado têm prioridade sobre a padrão até atingirem o
              teto do mês — havendo mais de uma, os pagamentos são distribuídos em rodízio entre elas;
              a padrão recebe o excedente.
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-2">
              Os pagamentos são repartidos pelo VALOR recebido no mês (cartão, PIX e transferência do CRM), na proporção dos pesos: com pesos 1 e 2,
              depois de R$ 5 mil na primeira empresa, os próximos R$ 10 mil vão para a segunda. O teto de cada
              empresa nunca é ultrapassado, e o bloqueio de transportadora vale acima do peso e do teto. Não há
              empresa padrão: pelo menos uma empresa ativa precisa ficar sem teto.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Regras por empresa */}
      {rules.length === 0 && (
        <Card>
          <CardContent>
            <p className="text-sm text-slate-500 text-center py-4">
              Nenhuma regra de roteamento configurada. Adicione uma empresa abaixo.
            </p>
          </CardContent>
        </Card>
      )}

      {routingMode === ROUTING_MODE_WEIGHTED && rules.length > 0 && (
        <WeightedRoutingPanel
          rules={rules}
          carrierServices={carrierServices}
          carrierLoadError={carrierLoadError}
          validation={weightedValidation}
          onChange={(companyId, changes) => updateRule(companyId, changes)}
          renderCities={companyId => renderCities(companyId, true)}
        />
      )}

      {routingMode === ROUTING_MODE_CAP_PRIORITY && rules.map(rule => {
        const capReachedThisMonth = rule.cap_reached_month != null && rule.cap_reached_month === currentMonth();
        return (
          <Card
            key={rule.company_id}
            className={rule.is_default ? 'border-emerald-200 bg-emerald-50/30' : undefined}
          >
            <CardContent>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-700">{rule.company_name}</h3>
                  <span className="text-xs text-slate-500 font-mono">{formatCnpj(rule.company_document)}</span>
                  {rule.is_default && <Badge variant="success">Padrão (fallback)</Badge>}
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="radio"
                      name="payment-routing-default"
                      className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={rule.is_default}
                      onChange={() => setDefaultCompany(rule.company_id)}
                    />
                    Empresa padrão
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={rule.enabled}
                      onChange={e => updateRule(rule.company_id, { enabled: e.target.checked })}
                    />
                    Ativa
                  </label>
                </div>
              </div>

              {capReachedThisMonth && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-red-700">
                    Teto atingido em {rule.cap_reached_on ? formatDateTimeBR(rule.cap_reached_on) : '—'} — pagamentos
                    roteados para a empresa padrão
                  </p>
                  <PermissionGuard permission="receiving_hub.write">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleClearCap(rule.company_id)}
                      loading={clearingCap === rule.company_id}
                    >
                      Reativar
                    </Button>
                  </PermissionGuard>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Teto de faturamento mensal (R$)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Sem teto"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={rule.monthly_revenue_cap}
                    onChange={e => updateRule(rule.company_id, { monthly_revenue_cap: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Vazio = sem teto</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    % de disparo
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={rule.cap_trigger_percent}
                    onChange={e => {
                      const v = Number(e.target.value);
                      updateRule(rule.company_id, { cap_trigger_percent: isNaN(v) ? 100 : v });
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">comuta ao atingir esse percentual do teto</p>
                </div>
              </div>

              <div className="mt-4">
                {renderCities(rule.company_id, false)}
                {rule.is_default && (
                  <p className="text-xs text-emerald-700 mt-1">
                    A empresa padrão nunca é restringida
                  </p>
                )}
              </div>

              <div className="mt-4">
                <CarrierBlocksEditor
                  options={carrierServices}
                  blocks={rule.carrier_blocks}
                  loadError={carrierLoadError}
                  onChange={blocks => updateRule(rule.company_id, { carrier_blocks: blocks })}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Neste modo o bloqueio vale só no ERP (cotação de frete, pedido de envio e emissão da NF desta
                  empresa). O roteamento do pagamento só considera a transportadora no balanceamento por peso.
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {routingMode === ROUTING_MODE_WEIGHTED && (
        <>
          <RoutingSimulatorCard carrierServices={carrierServices} />
          <RoutingDecisionsCard
            companies={rules.map(r => ({ company_id: r.company_id, company_name: r.company_name }))}
          />
        </>
      )}

      {/* Adicionar empresa sem regra */}
      {companiesWithoutRule.length > 0 && (
        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Adicionar empresa</h3>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm flex-1 min-w-[16rem]"
                value={newCompanyId}
                onChange={e => setNewCompanyId(Number(e.target.value))}
              >
                <option value={0}>Selecione...</option>
                {companiesWithoutRule.map(c => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name} — {formatCnpj(c.document)}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={addCompanyRule}
                disabled={!newCompanyId}
                icon={<Plus size={14} />}
              >
                Adicionar empresa
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              A nova regra só passa a valer após clicar em "Salvar configuração".
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
